/**
 * useMessageSocket
 * ----------------
 * Long-lived WebSocket to /api/ws/messages with:
 *   - automatic reconnect (exponential backoff, capped at 30s)
 *   - heartbeat replies (server pings every 25s, we respond pong)
 *   - per-subscriber callback registration (so multiple components can
 *     listen without each opening their own socket)
 *
 * Returns: { connected, subscribe(callback) → unsubscribe }
 *
 * Usage:
 *     const { connected, subscribe } = useMessageSocket();
 *     useEffect(() => subscribe((evt) => {
 *         if (evt.type === "message:new") setMessages((m) => [...m, evt.message]);
 *     }), [subscribe]);
 *
 * Falls back gracefully: when WS is unavailable, `connected` stays false
 * and callbacks never fire — UI keeps working via its existing poll.
 *
 * Cookie-based auth piggybacks on the existing session_token cookie
 * (same-origin in production AND on the preview host). For the rare case
 * where cookies don't ride along, we also send `?token=` derived from the
 * cookie if we can read it (we can't on httponly cookies, so the cookie
 * remains the primary auth path).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const POLL_FALLBACK = false; // hook reports `connected:false` so caller polls.

function wsUrlFromApiBase() {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    let url;
    try { url = new URL(apiBase); } catch { return null; }
    // Use the CURRENT host when the env-host doesn't match (matches the
    // same axios-rewrite trick in App.js for custom domains).
    if (url.host !== window.location.host) {
        url = new URL(window.location.origin);
    }
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = "/api/ws/messages";
    return url.toString();
}

export default function useMessageSocket() {
    const [connected, setConnected] = useState(false);
    const wsRef = useRef(null);
    const subscribersRef = useRef(new Set());
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const closedByUnmountRef = useRef(false);

    const subscribe = useCallback((cb) => {
        subscribersRef.current.add(cb);
        return () => { subscribersRef.current.delete(cb); };
    }, []);

    const open = useCallback(() => {
        const url = wsUrlFromApiBase();
        if (!url) return;
        try {
            const ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onopen = () => {
                reconnectAttemptsRef.current = 0;
                setConnected(true);
            };
            ws.onmessage = (e) => {
                let evt;
                try { evt = JSON.parse(e.data); } catch { return; }
                if (evt.type === "ping") {
                    try { ws.send(JSON.stringify({ type: "pong" })); } catch { /* noop */ }
                    return;
                }
                subscribersRef.current.forEach((cb) => {
                    try { cb(evt); } catch (err) {
                        // eslint-disable-next-line no-console
                        console.warn("[ws] subscriber error", err);
                    }
                });
            };
            ws.onerror = () => { /* onclose handles teardown */ };
            ws.onclose = () => {
                setConnected(false);
                wsRef.current = null;
                if (closedByUnmountRef.current) return;
                reconnectAttemptsRef.current += 1;
                const delay = Math.min(30000, 1000 * 2 ** Math.min(reconnectAttemptsRef.current, 5));
                reconnectTimerRef.current = setTimeout(open, delay);
            };
        } catch {
            // Silent — UI will fall back to polling.
            setConnected(false);
        }
    }, []);

    useEffect(() => {
        closedByUnmountRef.current = false;
        open();
        return () => {
            closedByUnmountRef.current = true;
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            const ws = wsRef.current;
            if (ws) {
                try { ws.close(); } catch { /* noop */ }
                wsRef.current = null;
            }
            subscribersRef.current.clear();
        };
    }, [open]);

    return { connected, subscribe, _pollFallback: POLL_FALLBACK };
}
